import api from "../../../utilidades/interceptor";

// URL base para el módulo de cotizaciones
const urlApi = process.env.REACT_APP_API_KEY || 'http://localhost:3000/';

/**
 * ============================================
 * SERVICIOS DE COTIZACIONES
 * ============================================
 */

/**
 * Crear una nueva cotización empresarial con sus turnos
 * @param {{nombre: string, idCliente: *, ticket: *, usuario: *, turnos: {idTurno: *, diasOperacion, horaInicio: string, horaFin: string, idTipoUnidad, tipoServicio: *, dinamica: *}[]}} quoteData - Datos de la cotización
 * @param {string} quoteData.nombre - Nombre de la cotización
 * @param {number} quoteData.idCliente - ID del cliente
 * @param {number} quoteData.ticket - ID del ticket asociado
 * @param {number} quoteData.usuario - ID del usuario que crea la cotización
 * @param {number} quoteData.tipoUnidad - ID del tipo de unidad/vehículo
 * @param {Array<Object>} quoteData.turnos - Array de turnos configurados
 * @param {number} quoteData.turnos[].idTurno - ID del turno (catálogo)
 * @param {string[]} quoteData.turnos[].diasOperacion - Días de operación (ej: ['LUNES', 'MARTES'])
 * @param {string} quoteData.turnos[].horaInicio - Hora de inicio formateada (HH:mm)
 * @param {string} quoteData.turnos[].horaFin - Hora de fin formateada (HH:mm)
 * @param {number} quoteData.turnos[].tipoServicio - ID del tipo de servicio
 * @param {boolean} quoteData.turnos[].dinamica - Indica si la ruta es dinámica
 * @param {string} quoteData.turnos[].fechaInicial - Fecha de inicio
 * @param {string} quoteData.turnos[].fechaFinal - Fecha de fin
 * @returns {Promise<{idCotizacion: number}>} ID de la cotización creada
 */
export const createQuote = async (quoteData) => {
    try {
        const result = await api.post(`${urlApi}/quotes/add`, quoteData);
        return result.data?.data;
    } catch (error) {
        console.error("Error al crear cotización:", error);
        throw error;
    }
};

/**
 * Analiza con IA los datos de una cotización antes de crearla
 * @param {Object} datos - Datos legibles de la cotización (nombres, no IDs)
 * @param {number} usuario - ID del usuario que crea la cotización
 * @returns {Promise<{requiereCambios: boolean|null, resultado: string|null, resumen: string, cambios: {campo: string, detalle: string}[], html: string, json: string|null}>}
 */
export const analizarCotizacionIA = async (datos, usuario) => {
    const result = await api.post(`${urlApi}/quotes/analisisIA`, { datos, usuario });
    return result.data?.data;
};

/**
 * Obtener listado de cotizaciones con filtros y paginación
 * @param {Object} filters - Filtros opcionales
 * @param {number} filters.page - Número de página (default: 1)
 * @param {number} filters.limit - Registros por página (default: 10)
 * @param {number} filters.clientId - Filtrar por cliente
 * @param {string} filters.status - Filtrar por estado (ACTIVO, BORRADOR, etc.)
 * @param {string} filters.searchTerm - Buscar por nombre
 * @returns {Promise<Object>} Lista de cotizaciones con paginación
 */
export const getQuotes = async (filters = {}) => {
    try {
        const params = new URLSearchParams();

        if (filters.page) params.append('page', filters.page);
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.clientId) params.append('clientId', filters.clientId);
        if (filters.status) params.append('status', filters.status);
        if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);

        const queryString = params.toString();
        const url = queryString ? `${urlApi}/quotes?${queryString}` : `${urlApi}/quotes`;

        const result = await api.get(url);
        return result.data;
    } catch (error) {
        console.error("Error al obtener cotizaciones:", error);
        throw error;
    }
};

/**
 * Obtener una cotización por ID con todos sus turnos
 * @param {number} id - ID de la cotización
 * @returns {Promise<Object>} Cotización completa con turnos
 */
export const getQuoteById = async (id) => {
    try {
        const result = await api.get(`${urlApi}/quotes/${id}`);
        return result.data;
    } catch (error) {
        console.error(`Error al obtener cotización ${id}:`, error);
        throw error;
    }
};

/**
 * Actualizar una cotización existente
 * @param {number} id - ID de la cotización
 * @param {Object} updateData - Datos a actualizar
 * @returns {Promise<Object>} Resultado de la actualización
 */
export const updateQuote = async (id, updateData) => {
    try {
        const result = await api.put(`${urlApi}/quotes/${id}`, updateData);
        return result.data;
    } catch (error) {
        console.error(`Error al actualizar cotización ${id}:`, error);
        throw error;
    }
};

/**
 * Eliminar una cotización (soft delete)
 * @param {number} id - ID de la cotización
 * @returns {Promise<Object>} Confirmación de eliminación
 */
export const deleteQuote = async (id) => {
    try {
        const result = await api.delete(`${urlApi}/quotes/${id}`);
        return result.data;
    } catch (error) {
        console.error(`Error al eliminar cotización ${id}:`, error);
        throw error;
    }
};

/**
 * ============================================
 * CATÁLOGOS Y DATOS AUXILIARES
 * ============================================
 */

/**
 * Obtener listado de clientes para dropdown
 * @returns {Promise<Array>} Lista de clientes
 */
export const getClientsCatalog = async () => {
    try {
        const result = await api.get(`${urlApi}/quotes/catalogs/clients`);
        return result.data;
    } catch (error) {
        console.error("Error al obtener catálogo de clientes:", error);
        throw error;
    }
};

/**
 * Obtener listado de tipos de vehículos
 * @returns {Promise<Array>} Lista de tipos de vehículos
 */
export const getVehicleTypesCatalog = async () => {
    try {
        const result = await api.get(`${urlApi}/quotes/catalogs/vehicle-types`);
        return result.data;
    } catch (error) {
        console.error("Error al obtener catálogo de vehículos:", error);
        throw error;
    }
};

/**
 * Obtener listado de tickets
 * @returns {Promise<Array>} Lista de tickets de cada cliente
 */
export const getTicketsList = async (client, cat) => {
    try {
        const result = await api.get(`${urlApi}/quotes/catalogs/tickets?client=${client}&cat=${cat}`);
        return result.data;
    } catch (error) {
        console.error("Error al obtener catálogo de vehículos:", error);
        throw error;
    }
};


/**
 * Obtener listado de rutas
 * @returns {Promise<Array>} Lista de rutas
 */
export const getRoutesList = async (client) => {
    try {
        const result = await api.get(`${urlApi}/quotes/catalogs/routes?client=${client}`);
        return result.data;
    } catch (error) {
        console.error("Error al obtener catálogo de vehículos:", error);
        throw error;
    }
};

/**
 * Obtener listado de tipos de turno
 * @returns {Promise<Array>} Lista de tipos de turno
 */
export const getShiftsList = async () => {
    try {
        const result = await api.get(`${urlApi}/quotes/catalogs/shifts-types`);
        return result.data;
    } catch (error) {
        console.error("Error al obtener catálogo de turnos:", error);
        throw error;
    }
};

/**
 * Obtener listado de tipos de servicio
 * @returns {Promise<Array>} Lista de tipos de servicio
 */
export const getServiceList = async () => {
    try {
        const result = await api.get(`${urlApi}/quotes/catalogs/service-types`);
        return result.data;
    } catch (error) {
        console.error("Error al obtener catálogo de turnos:", error);
        throw error;
    }
};

/**
 * Obtener listado de tipos de cambios
 * @returns {Promise<Array>} Lista de tipos de cambios
 */
export const getChangeTypes = async () => {
    try {
        const result = await api.get(`${urlApi}/quotes/catalogs/change-types`);
        return result.data;
    } catch (error) {
        console.error("Error al obtener catálogo de turnos:", error);
        throw error;
    }
};

/**
 * Copiar los datos y mandarlos a produccion
 * @returns {Promise<Object>} Resultado de copiar
 * @param rutaId
 * @param usuarioId
 * @param fechaDesde
 */
export const sendToProduction = async (rutaId, usuarioId, fechaDesde) => {
    try {
        const result = await api.post(`${urlApi}/quotes/route`, { rutaId, usuarioId, fechaDesde });
        return result.data;
    } catch (error) {
        console.error(`Error al copiar la ruta ${rutaId}:`, error);
        throw error;
    }
};


/**
 * Copiar los datos y mandarlos a producción
 * @returns {Promise<Object>} Resultado de copiar
 * @param rutaId
 * @param usuarioId
 * @param idCot
 * @param fechaDesde
 */
export const sendToProductionMod = async (rutaId, usuarioId, idCot, fechaDesde) => {
    try {
        const result = await api.put(`${urlApi}/quotes/sendQuoteFromRouteToProd`, { rutaId, usuarioId, idCot, fechaDesde });
        return result.data;
    } catch (error) {
        console.error(`Error al copiar la ruta ${rutaId}:`, error);
        throw error;
    }
};

/**
 * ============================================
 * FUNCIONES AUXILIARES
 * ============================================
 */


/**
 * Formatear hora de Date a string HH:MM
 * @param {Date|string} fecha - Fecha u hora
 * @returns {string} Hora en formato HH:MM
 */
export const formatHoraForBackend = (fecha) => {
    if (!fecha) return '';

    if (typeof fecha === 'string') return fecha;

    if (fecha instanceof Date) {
        return fecha.toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    }

    return '';
};
